// src/pages/BlogsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, ArrowRight } from 'lucide-react';
import Button from '../components/ui/Button';
import client from '../utils/sanityClient';
import { getSafeImageUrl, SafeImage } from '../utils/imageUtils';

const BlogsPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Fetch blog posts from Sanity
    const fetchBlogPosts = async () => {
      try {
        // GROQ query to fetch all blog posts based on your schema
        const query = `*[_type == "post"] | order(publishedAt desc) {
          _id,
          title,
          "slug": slug.current,
          excerpt,
          publishedAt,
          readTime,
          category,
          "author": author->name,
          "authorRole": author->role,
          mainImage,
          // Check if this post appears in any other post's relatedPosts
          // This is a way to identify "featured" posts
          "isFeatured": count(*[_type == "post" && references(^._id)]) > 0
        }`;
        
        const posts = await client.fetch(query);
        
        // If no posts found, use dummy data for development
        if (!posts || posts.length === 0) {
          console.log('No blog posts found in Sanity, using dummy data');
          // Sample blog data for development
          const samplePosts = [
            {
              _id: 'sample-1',
              title: 'The Weight of Justice',
              subtitle: 'A Comparative Analysis of Judicial Caseloads in Common Law Jurisdictions',
              slug: 'the-weight-of-justice',
              excerpt: 'The Indian judicial system faces an unprecedented backlog of cases across all levels of courts. This article presents a comparative analysis of judicial caseloads between India and other common law jurisdictions.',
              publishedAt: new Date().toISOString(),
              readTime: '15 min read',
              category: 'Legal Research',
              author: 'Adv Vibhor Gupta',
              authorRole: 'Managing Partner, AMV LAW',
              isFeatured: true
            },
            {
              _id: 'sample-2',
              title: 'Artificial Intelligence: Transforming the Legal Practice',
              subtitle: 'How AI is Reshaping Legal Research, Contract Analysis, and Case Prediction',
              slug: 'ai-in-legal-practice',
              excerpt: 'This article explores how artificial intelligence is revolutionizing legal practice with applications in legal research, document review, contract analysis, and predictive analytics for case outcomes.',
              publishedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
              readTime: '10 min read',
              category: 'Legal AI',
              author: 'Adv Mehak Gupta',
              authorRole: 'Managing Partner, AMV LAW',
              isFeatured: false
            }
          ];
          setBlogPosts(samplePosts);
        } else {
          setBlogPosts(posts);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBlogPosts();
  }, []);
  
  // Handle loading state
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }
  
  // Handle error state
  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center p-4">
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => window.location.reload()} variant="primary">
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  // Separate featured blog from the rest
  // For simplicity, just take the first post as featured if no post is marked as featured
  const featuredBlog = blogPosts.find(post => post.isFeatured) || blogPosts[0];
  const regularBlogs = blogPosts.filter(post => post !== featuredBlog);
  
  // Format date to match the original format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4 text-center">AMV LAW Insights</h1>
          <p className="text-xl text-center max-w-3xl mx-auto">
            Thoughtful analyses and perspectives on legal developments, practice innovations, and industry trends
          </p>
        </div>
      </div>

      {/* Featured Blog Post */}
      {featuredBlog && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl font-serif">Featured Article</h2>
              <div className="w-20 h-1 bg-secondary mx-auto my-4"></div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-4xl mx-auto">
              <div className="p-8">
                <div className="mb-4">
                  <span className="bg-primary-50 text-primary px-3 py-1 rounded-full text-sm font-medium">
                    {featuredBlog.category}
                  </span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {featuredBlog.title}
                </h3>
                
                <p className="text-lg text-gray-600 mb-6">
                  {featuredBlog.subtitle}
                </p>

                <div className="flex flex-wrap items-center text-gray-500 text-sm mb-6">
                  <div className="flex items-center mr-4 mb-2">
                    <User className="h-4 w-4 mr-1" />
                    <span>{featuredBlog.author}</span>
                  </div>
                  <div className="flex items-center mr-4 mb-2">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{formatDate(featuredBlog.publishedAt)}</span>
                  </div>
                  <div className="flex items-center mb-2">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{featuredBlog.readTime || '10 min read'}</span>
                  </div>
                </div>

                <p className="text-gray-700 mb-6">
                  {featuredBlog.excerpt}
                </p>

                <Link
                  to={`/blogs/${featuredBlog.slug}`}
                  className="inline-flex items-center text-secondary hover:text-secondary-dark transition-colors"
                >
                  Read More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Recent Blog Posts */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl font-serif">Recent Articles</h2>
            <div className="w-20 h-1 bg-secondary mx-auto my-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularBlogs.map((post) => (
              <div 
                key={post._id} 
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 h-full flex flex-col"
              >
                {post.mainImage && (
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    <SafeImage 
                      image={post.mainImage}
                      alt={post.mainImage.alt || post.title}
                      className="w-full h-full object-cover"
                      fallbackText="Blog thumbnail"
                    />
                  </div>
                )}
                
                <div className="p-6 flex-grow flex flex-col">
                  <div className="mb-3">
                    <span className="bg-primary-50 text-primary px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {post.title}
                  </h3>
                  
                  <div className="flex flex-wrap items-center text-gray-500 text-sm mb-4">
                    <div className="flex items-center mr-4 mb-2">
                      <User className="h-4 w-4 mr-1" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(post.publishedAt)}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4 flex-grow">
                    {post.excerpt && post.excerpt.length > 150 
                      ? `${post.excerpt.substring(0, 150)}...` 
                      : post.excerpt}
                  </p>
                  
                  <Link
                    to={`/blogs/${post.slug}`}
                    className="inline-flex items-center text-secondary hover:text-secondary-dark transition-colors mt-auto"
                  >
                    Read Article
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          {regularBlogs.length === 0 && !featuredBlog && (
            <div className="text-center py-12">
              <p className="text-gray-500">No blog posts available at the moment. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Stay Updated with Our Legal Insights</h2>
          <p className="mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter to receive the latest articles, case analyses, and legal updates directly in your inbox.
          </p>
          
          <div className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                placeholder="Your email address"
                className="px-4 py-3 rounded-lg w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <Button 
                variant="accent"
                size="lg"
                className="whitespace-nowrap"
              >
                Subscribe
              </Button>
            </div>
            <p className="text-sm text-gray-300 mt-3">
              We respect your privacy. You can unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogsPage;